import React, { useState, useEffect } from 'react';
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { format, parseISO, addHours, addDays, differenceInHours, differenceInMinutes, isWithinInterval, startOfDay, endOfDay, addDays as addDaysDate } from 'date-fns';
import { es } from 'date-fns/locale';
import { Trash2, User, ListTodo, Bell, CalendarDays } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useOverdueTasks } from './NotificationBanner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DatePicker } from '@/components/DatePicker';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';

interface KanbanCard {
  id: string;
  title: string;
  due_date: string;
  status: string;
  client_name?: string | null;
  assigned_to_id?: string | null;
  responsible?: string;
  archived?: boolean;
}

interface Reminder {
  id: string;
  title: string;
  due_date: string;
}

interface ProgressReminder {
  id: string;
  organization_name: string;
  reminder_text: string;
  due_date: string;
  milestone_days: number;
}

interface TaskSuggestion {
  id: string;
  task_name: string;
  client_organization: string;
  suggested_date: string;
  work_log_id: string;
  created_at: string;
  is_processed: boolean;
  visibility: string;
}

type ItemType = 'task' | 'reminder' | 'progress' | 'suggestion';

interface OverdueItem {
  id: string;
  title: string;
  type: ItemType;
  priority: 'high' | 'medium';
  dueDate: string;
  overdueText: string;
  organizationName?: string;
  milestoneDays?: number;
  clientName?: string | null;
  assignedToName?: string | null;
  responsible?: string | null;
}

export const OverdueTasksDialog: React.FC = () => {
  const { showOverdueDialog, setShowOverdueDialog, refreshOverdueTasks } = useOverdueTasks();
  const [overdueKanbanCards, setOverdueKanbanCards] = useState<KanbanCard[]>([]);
  const [overdueReminders, setOverdueReminders] = useState<Reminder[]>([]);
  const [overdueProgressReminders, setOverdueProgressReminders] = useState<ProgressReminder[]>([]);
  const [taskSuggestions, setTaskSuggestions] = useState<TaskSuggestion[]>([]);
  const [consolidatedItems, setConsolidatedItems] = useState<OverdueItem[]>([]);
  const [visibleItems, setVisibleItems] = useState<OverdueItem[]>([]);
  const [hiddenItems, setHiddenItems] = useState<OverdueItem[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [initialCheck, setInitialCheck] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [deletingItemType, setDeletingItemType] = useState<'task' | 'reminder' | 'progress' | 'suggestion' | null>(null);
  const [assigneeNames, setAssigneeNames] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState('tasks');
  const [thisWeekItems, setThisWeekItems] = useState<OverdueItem[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [currentSuggestion, setCurrentSuggestion] = useState<string | null>(null);
  const [orgUsers, setOrgUsers] = useState<{id: string, first_name: string, last_name: string}[]>([]);

  const { user } = useAuth();
  const { toast } = useToast();

  const isAdminUser = user?.email?.endsWith('@revenuehublatam.com') || user?.email?.endsWith('@digitalgrowth.cl');

  const getOverdueText = (dueDate: string): string => {
    const now = new Date();
    const dueDateObj = new Date(dueDate);
    
    if (dueDateObj > now) return ""; // Not overdue
    
    const hoursDiff = differenceInHours(now, dueDateObj);
    
    if (hoursDiff < 24) {
      const minutesDiff = differenceInMinutes(now, dueDateObj) % 60;
      if (hoursDiff === 0) {
        return `Vencido hace ${minutesDiff} minutos`;
      } else {
        return `Vencido hace ${hoursDiff} horas y ${minutesDiff} minutos`;
      }
    } else {
      const days = Math.floor(hoursDiff / 24);
      const remainingHours = hoursDiff % 24;
      return `Vencido hace ${days} días y ${remainingHours} horas`;
    }
  };

  useEffect(() => {
    const fetchOrgUsers = async () => {
      try {
        if (!user?.organization_name) return;
        
        const { data, error } = await supabase
          .from('profiles')
          .select('id, first_name, last_name')
          .eq('organization_name', user.organization_name);
        
        if (error) {
          console.error('Error fetching organization users:', error);
          throw error;
        }
        
        setOrgUsers(data || []);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };

    if (user) {
      fetchOrgUsers();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchOverdueTasks();
      fetchTaskSuggestions();
    }
  }, [user]);

  useEffect(() => {
    if (!initialCheck && user) {
      const hasOverdueItems = overdueKanbanCards.length > 0 || 
                             overdueReminders.length > 0 || 
                             overdueProgressReminders.length > 0;
      
      if (hasOverdueItems) {
        setShowOverdueDialog(true);
      }
      
      setInitialCheck(true);
    }
  }, [overdueKanbanCards, overdueReminders, overdueProgressReminders, initialCheck, user, setShowOverdueDialog]);

  useEffect(() => {
    const fetchAssigneeNames = async () => {
      const assigneeIds = overdueKanbanCards
        .filter(card => card.assigned_to_id)
        .map(card => card.assigned_to_id as string);
      
      if (assigneeIds.length === 0) return;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, first_name, last_name')
          .in('id', assigneeIds);
        
        if (error) throw error;
        
        const namesMap: Record<string, string> = {};
        
        data?.forEach(profile => {
          const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
          namesMap[profile.id] = fullName;
        });
        
        setAssigneeNames(namesMap);
      } catch (error) {
        console.error("Error fetching assignee names:", error);
      }
    };
    
    if (overdueKanbanCards.length > 0) {
      fetchAssigneeNames();
    }
  }, [overdueKanbanCards]);

  useEffect(() => {
    const allItems: OverdueItem[] = [
      ...overdueKanbanCards.map(card => ({
        id: card.id,
        title: card.title,
        type: 'task' as const,
        priority: 'high' as const,
        dueDate: card.due_date,
        overdueText: getOverdueText(card.due_date),
        clientName: card.client_name,
        assignedToName: card.assigned_to_id ? assigneeNames[card.assigned_to_id] : undefined,
        responsible: card.responsible
      })),
      ...overdueReminders.map(reminder => ({
        id: reminder.id,
        title: reminder.title,
        type: 'reminder' as const,
        priority: 'medium' as const,
        dueDate: reminder.due_date,
        overdueText: getOverdueText(reminder.due_date)
      })),
      ...overdueProgressReminders.map(reminder => ({
        id: reminder.id,
        title: reminder.reminder_text,
        type: 'progress' as const,
        priority: 'medium' as const,
        dueDate: reminder.due_date,
        overdueText: getOverdueText(reminder.due_date),
        organizationName: reminder.organization_name,
        milestoneDays: reminder.milestone_days
      }))
    ];
    
    allItems.sort((a, b) => {
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    });
    
    setConsolidatedItems(allItems);
  }, [overdueKanbanCards, overdueReminders, overdueProgressReminders, assigneeNames]);

  useEffect(() => {
    if (consolidatedItems.length > 4 && !showAll) {
      setVisibleItems(consolidatedItems.slice(0, 4));
      setHiddenItems(consolidatedItems.slice(4));
    } else {
      setVisibleItems(consolidatedItems);
      setHiddenItems([]);
    }
  }, [consolidatedItems, showAll]);

  useEffect(() => {
    const now = new Date();
    const weekEnd = addDaysDate(now, 7);
    
    const weekItems = consolidatedItems.filter(item => {
      const itemDate = new Date(item.dueDate);
      return isWithinInterval(itemDate, { start: startOfDay(now), end: endOfDay(weekEnd) });
    });
    
    setThisWeekItems(weekItems);
  }, [consolidatedItems]);

  const fetchTaskSuggestions = async () => {
    if (!user) return;
    
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_name')
        .eq('id', user.id)
        .single();
      
      if (!profile?.organization_name) {
        console.error("Could not find organization for user");
        return;
      }
      
      let query;
      if (profile.organization_name === 'Revenue Hub') {
        query = supabase
          .from('tasks_suggestions')
          .select('*')
          .eq('visibility', 'Revenue Hub');
      } else {
        query = supabase
          .from('tasks_suggestions')
          .select('*')
          .eq('client_organization', profile.organization_name)
          .eq('visibility', 'Client');
      }
      
      const { data: suggestions, error } = await query;
      
      if (error) {
        console.error("Error fetching task suggestions:", error);
        return;
      }
      
      setTaskSuggestions(suggestions || []);
    } catch (error) {
      console.error("Error fetching task suggestions:", error);
    }
  };

  const fetchOverdueTasks = async () => {
    if (!user) return;
    
    const now = new Date();
    
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_name')
        .eq('id', user.id)
        .single();
      
      if (!profile?.organization_name) {
        console.error("Could not find organization for user");
        return;
      }
      
      const { data: kanbanCards, error: kanbanError } = await supabase
        .from('kanban_cards')
        .select('id, title, due_date, status, client_name, assigned_to_id, responsible, archived')
        .eq('organization_name', profile.organization_name)
        .neq('status', 'Terminado')
        .eq('archived', false) // Only fetch non-archived cards
        .not('due_date', 'is', null);
      
      if (kanbanError) {
        console.error("Error fetching kanban cards:", kanbanError);
        return;
      }
      
      const overdue = kanbanCards?.filter(card => {
        if (!card.due_date) return false;
        const cardDueDate = new Date(card.due_date);
        return cardDueDate < now;
      }) || [];
      
      setOverdueKanbanCards(overdue);
      
      const { data: reminders, error: reminderError } = await supabase
        .from('reminders')
        .select('id, title, due_date')
        .eq('user_id', user.id)
        .not('due_date', 'is', null);
      
      if (reminderError) {
        console.error("Error fetching reminders:", reminderError);
        return;
      }
      
      const overdueReminders = reminders?.filter(reminder => {
        if (!reminder.due_date) return false;
        const reminderDueDate = new Date(reminder.due_date);
        return reminderDueDate < now;
      }) || [];
      
      setOverdueReminders(overdueReminders);
      
      if (isAdminUser) {
        const { data: progressReminders, error: progressError } = await supabase
          .from('focusflow_progress_reminders')
          .select('id, organization_name, reminder_text, due_date, milestone_days')
          .not('due_date', 'is', null);
        
        if (progressError) {
          console.error("Error fetching progress reminders:", progressError);
          return;
        }
        
        const overdueProgressReminders = progressReminders?.filter(reminder => {
          if (!reminder.due_date) return false;
          const reminderDueDate = new Date(reminder.due_date);
          return reminderDueDate < now;
        }) || [];
        
        setOverdueProgressReminders(overdueProgressReminders);
      }
      
    } catch (error) {
      console.error("Error fetching overdue tasks:", error);
    }
  };

  const handlePostponeHours = async (itemId: string, hours: number, itemType: ItemType) => {
    try {
      const targetItem = consolidatedItems.find(item => item.id === itemId);
      if (!targetItem) return;
      
      const newDate = addHours(new Date(), hours);
      
      if (itemType === 'task') {
        const { error } = await supabase
          .from('kanban_cards')
          .update({ due_date: newDate.toISOString() })
          .eq('id', itemId);
        
        if (error) throw error;
      } else if (itemType === 'reminder') {
        const { error } = await supabase
          .from('reminders')
          .update({ due_date: newDate.toISOString() })
          .eq('id', itemId);
        
        if (error) throw error;
      } else if (itemType === 'progress') {
        const { error } = await supabase
          .from('focusflow_progress_reminders')
          .update({ due_date: newDate.toISOString() })
          .eq('id', itemId);
        
        if (error) throw error;
      }
      
      setConsolidatedItems(prev => prev.filter(item => item.id !== itemId));
      
      refreshOverdueTasks();
      
      toast({
        title: "Fecha actualizada",
        description: `El elemento ha sido pospuesto por ${hours} horas.`,
      });
      
      closeDialogIfEmpty();
    } catch (error) {
      console.error("Error postponing item:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo actualizar la fecha.",
      });
    }
  };

  const handlePostponeDays = async (itemId: string, days: number, itemType: ItemType) => {
    try {
      const targetItem = consolidatedItems.find(item => item.id === itemId);
      if (!targetItem) return;
      
      const newDate = addDays(new Date(), days);
      
      if (itemType === 'task') {
        const { error } = await supabase
          .from('kanban_cards')
          .update({ due_date: newDate.toISOString() })
          .eq('id', itemId);
        
        if (error) throw error;
      } else if (itemType === 'reminder') {
        const { error } = await supabase
          .from('reminders')
          .update({ due_date: newDate.toISOString() })
          .eq('id', itemId);
        
        if (error) throw error;
      } else if (itemType === 'progress') {
        const { error } = await supabase
          .from('focusflow_progress_reminders')
          .update({ due_date: newDate.toISOString() })
          .eq('id', itemId);
        
        if (error) throw error;
      }
      
      setConsolidatedItems(prev => prev.filter(item => item.id !== itemId));
      
      refreshOverdueTasks();
      
      toast({
        title: "Fecha actualizada",
        description: `El elemento ha sido pospuesto por ${days} día(s).`,
      });
      
      closeDialogIfEmpty();
    } catch (error) {
      console.error("Error postponing item:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo actualizar la fecha.",
      });
    }
  };

  const confirmDelete = (itemId: string, itemType: ItemType) => {
    setDeletingItemId(itemId);
    setDeletingItemType(itemType);
  };

  const handleDelete = async () => {
    if (!deletingItemId || !deletingItemType) return;
    
    try {
      if (deletingItemType === 'task') {
        const { error } = await supabase
          .from('kanban_cards')
          .update({ 
            archived: true,
            archived_at: new Date().toISOString() 
          })
          .eq('id', deletingItemId);
        
        if (error) throw error;
      } else if (deletingItemType === 'reminder') {
        const { error } = await supabase
          .from('reminders')
          .delete()
          .eq('id', deletingItemId);
        
        if (error) throw error;
      } else if (deletingItemType === 'progress') {
        const { error } = await supabase
          .from('focusflow_progress_reminders')
          .delete()
          .eq('id', deletingItemId);
        
        if (error) throw error;
      } else if (deletingItemType === 'suggestion') {
        const { error } = await supabase
          .from('tasks_suggestions')
          .delete()
          .eq('id', deletingItemId);
        
        if (error) throw error;
        
        fetchTaskSuggestions();
      }
      
      if (deletingItemType !== 'suggestion') {
        setConsolidatedItems(prev => prev.filter(item => item.id !== deletingItemId));
        refreshOverdueTasks();
      }
      
      let messageText = "El elemento ha sido eliminado correctamente.";
      if (deletingItemType === 'task') messageText = "La tarea ha sido archivada correctamente.";
      if (deletingItemType === 'reminder') messageText = "El recordatorio ha sido eliminado correctamente.";
      
      toast({
        title: deletingItemType === 'task' ? "Archivado" : "Eliminado",
        description: messageText,
      });
      
      setDeletingItemId(null);
      setDeletingItemType(null);
      
      closeDialogIfEmpty();
    } catch (error) {
      console.error("Error deleting item:", error);
      toast({
        title: "Error",
        description: "No se pudo procesar la operación.",
        variant: "destructive",
      });
      
      setDeletingItemId(null);
      setDeletingItemType(null);
    }
  };

  const createTaskFromSuggestion = async (suggestionId: string) => {
    if (!selectedUser || !selectedDate) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Selecciona un usuario y una fecha de vencimiento",
      });
      return;
    }
    
    try {
      const suggestion = taskSuggestions.find(s => s.id === suggestionId);
      if (!suggestion) return;
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_name')
        .eq('id', user?.id)
        .single();
      
      if (!profile?.organization_name) {
        throw new Error("Could not find organization for user");
      }
      
      const { data: assignedUser, error: userError } = await supabase
        .from('profiles')
        .select('email, first_name, last_name')
        .eq('id', selectedUser)
        .single();
        
      if (userError) throw userError;
      
      const { data: assignerData, error: assignerError } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', user?.id || '')
        .single();
        
      if (assignerError) throw assignerError;
      
      const assignerName = `${assignerData.first_name || ''} ${assignerData.last_name || ''}`.trim();
      
      const { data: newCard, error: createError } = await supabase
        .from('kanban_cards')
        .insert({
          title: suggestion.task_name,
          status: 'Por hacer',
          organization_name: profile.organization_name,
          responsible: 'Admin',
          client_name: profile.organization_name === 'Revenue Hub' ? suggestion.client_organization : 'Revenue Hub',
          priority: 'Alta',
          user_id: selectedUser,
          assigned_to_id: selectedUser,
          position: 999,
          due_date: selectedDate.toISOString()
        })
        .select()
        .single();
      
      if (createError) throw createError;
      
      const { error: deleteError } = await supabase
        .from('tasks_suggestions')
        .delete()
        .eq('id', suggestion.id);
      
      if (deleteError) throw deleteError;
      
      try {
        const formattedDate = selectedDate.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        
        await supabase.functions.invoke('send-notification-task-assigned', {
          body: {
            assignedToEmail: assignedUser.email,
            assignedToFirstName: assignedUser.first_name || 'Usuario',
            assignedBy: assignerName || 'Revenue Hub',
            taskTitle: suggestion.task_name,
            dueDate: formattedDate,
            misTareasUrl: 'https://app.revenuehublatam.com/workspace/tasks'
          }
        });
      } catch (notificationError) {
        console.error('Error sending notification:', notificationError);
      }
      
      toast({
        title: "Tarea creada",
        description: "La tarea ha sido creada exitosamente.",
      });
      
      setSelectedUser(null);
      setSelectedDate(undefined);
      setCurrentSuggestion(null);
      fetchTaskSuggestions();
      
    } catch (error) {
      console.error('Error creating task from suggestion:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo crear la tarea."
      });
    }
  };

  const cancelDelete = () => {
    setDeletingItemId(null);
    setDeletingItemType(null);
  };

  const closeDialogIfEmpty = () => {
    if (consolidatedItems.length <= 1) {
      setShowOverdueDialog(false);
    }
  };

  const handleRemindLater = () => {
    setShowOverdueDialog(false);
    
    setTimeout(() => {
      if (consolidatedItems.length > 0) {
        setShowOverdueDialog(true);
      }
    }, 3 * 60 * 60 * 1000);
  };

  const overdueTasksCount = consolidatedItems.filter(item => item.type === 'task').length;
  const overdueRemindersCount = consolidatedItems.filter(item => 
    item.type === 'reminder' || item.type === 'progress'
  ).length;
  const suggestionCount = taskSuggestions.length;

  const renderOverdueItem = (item: OverdueItem) => (
    <div className="bg-white border border-gray-200 rounded-lg mb-3 relative overflow-hidden" key={item.id}>
      <div className={`absolute top-0 left-0 w-1.5 h-full ${
        item.priority === 'high' ? 'bg-red-500' : 'bg-orange-500'
      }`}></div>
      <div className="absolute top-3 right-3 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center font-bold text-xs">
        !
      </div>
      <div className="p-4 pl-5">
        {isAdminUser && item.type === 'task' && item.clientName && (
          <div className="text-xs text-gray-600 mb-1">
            Cliente: {item.clientName}
          </div>
        )}
        <h3 className="text-base font-semibold text-gray-800 mb-2 pr-6">{item.title}</h3>
        <div className="flex items-center mb-3 flex-wrap gap-1">
          <span className={`px-2 py-0.5 rounded-full text-xs ${
            item.type === 'task' ? 'bg-blue-100 text-blue-700' : 
            item.type === 'reminder' ? 'bg-indigo-100 text-indigo-700' : 'bg-purple-100 text-purple-700'
          }`}>
            {item.type === 'task' ? 'Tarea' : 
             item.type === 'reminder' ? 'Recordatorio' : 'Progreso'}
          </span>
          <span className="text-xs text-red-500 ml-1">
            {item.overdueText}
          </span>
        </div>
        {item.organizationName && (
          <div className="text-xs text-gray-500 mb-2">
            Organización: {item.organizationName}
            {item.milestoneDays && ` • ${item.milestoneDays} días`}
          </div>
        )}
        <div className="flex justify-between items-end">
          <div className="flex gap-2 flex-wrap">
            <button 
              className="py-1 px-3 text-xs font-medium rounded-full bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100"
              onClick={() => handlePostponeHours(item.id, 3, item.type)}
            >
              +3 horas
            </button>
            <button 
              className="py-1 px-3 text-xs font-medium rounded-full bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100"
              onClick={() => handlePostponeDays(item.id, 1, item.type)}
            >
              +1 día
            </button>
            <button 
              className="w-6 h-6 rounded-full flex items-center justify-center bg-gray-100 text-gray-500 border border-gray-200 hover:bg-gray-200"
              onClick={() => confirmDelete(item.id, item.type)}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
          {(item.type === 'task' && (item.assignedToName || item.responsible)) && (
            <div className="flex items-center text-xs text-gray-600">
              <User className="h-3 w-3 mr-1" />
              <span>{item.assignedToName || item.responsible}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSuggestionItem = (suggestion: TaskSuggestion) => (
    <div 
      className={`border rounded-md p-3 bg-white shadow-sm hover:shadow-md transition-shadow mb-3 ${
        currentSuggestion === suggestion.id ? 'border-blue-500' : 'border-gray-200'
      }`}
      key={suggestion.id}
    >
      {currentSuggestion === suggestion.id ? (
        <>
          <div className="mb-2">
            <h3 className="font-medium text-gray-900">{suggestion.task_name}</h3>
            <p className="text-xs text-gray-500">
              {user?.organization_name === 'Revenue Hub' ? 'Cliente' : 'Para'}: 
              <span className="font-medium"> {suggestion.client_organization}</span>
            </p>
          </div>
          
          <div className="pt-2 border-t border-gray-100">
            <div className="flex flex-col gap-2">
              <Select value={selectedUser || ''} onValueChange={setSelectedUser}>
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue placeholder="Asignar a" />
                </SelectTrigger>
                <SelectContent>
                  {orgUsers.map(orgUser => (
                    <SelectItem key={orgUser.id} value={orgUser.id} className="text-xs">
                      {orgUser.first_name} {orgUser.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="w-full">
                <DatePicker
                  date={selectedDate}
                  onDateChange={setSelectedDate}
                  className="h-7 text-xs"
                  placeholder="Fecha de vencimiento"
                  allowPastDates={false}
                />
              </div>

              <div className="flex gap-1">
                <button 
                  className="py-1 px-3 text-xs font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50 flex-1"
                  onClick={() => setCurrentSuggestion(null)}
                >
                  Cancelar
                </button>
                <button 
                  className="py-1 px-3 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 flex-1 disabled:opacity-50"
                  onClick={() => createTaskFromSuggestion(suggestion.id)}
                  disabled={!selectedUser || !selectedDate}
                >
                  Crear
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex items-start">
          <div className="flex-1 min-w-0 pr-2">
            <h3 className="font-medium text-gray-900 text-sm">{suggestion.task_name}</h3>
            <div className="flex flex-wrap items-center gap-x-2 mt-1">
              {user?.organization_name === 'Revenue Hub' && (
                <p className="text-xs text-gray-500">
                  <span className="font-medium">{suggestion.client_organization}</span>
                </p>
              )}
              <p className="text-xs text-gray-400">
                {new Date(suggestion.suggested_date).toLocaleDateString('es-ES')}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-1 shrink-0">
            <button 
              className="h-6 w-6 p-0 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full flex items-center justify-center"
              onClick={() => confirmDelete(suggestion.id, 'suggestion')}
            >
              <Trash2 className="h-3.5 w-3.5" />
              <span className="sr-only">Rechazar</span>
            </button>
            <button 
              className="h-6 w-6 p-0 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-full flex items-center justify-center"
              onClick={() => setCurrentSuggestion(suggestion.id)}
            >
              <User className="h-3.5 w-3.5" />
              <span className="sr-only">Asignar</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (!showOverdueDialog) return null;

  const totalItems = consolidatedItems.length;

  return (
    <div className="fixed inset-0 bg-black/70 z-[1000] flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg w-[600px] max-w-[95vw] overflow-hidden">
        <div className="p-5 border-b border-gray-200 relative">
          <div className="bg-red-500 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-xl absolute top-5 left-5">
            {totalItems}
          </div>
          <div className="ml-14">
            <h2 className="text-xl font-bold text-gray-800">Tareas y Recordatorios Vencidos</h2>
            <p className="text-sm text-gray-500">Actualizaciones necesarias</p>
          </div>
        </div>
        
        <div className="bg-gray-50 max-h-[400px] overflow-hidden">
          <Tabs defaultValue="tasks" className="w-full" value={activeTab} onValueChange={setActiveTab}>
            <div className="px-4 pt-4">
              <TabsList className="w-full">
                <TabsTrigger value="tasks" className="flex-1">
                  <ListTodo className="w-4 h-4 mr-2" />
                  Tareas
                  {overdueTasksCount > 0 && (
                    <Badge variant="destructive" className="ml-2">{overdueTasksCount}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="reminders" className="flex-1">
                  <Bell className="w-4 h-4 mr-2" />
                  Recordatorios
                  {overdueRemindersCount > 0 && (
                    <Badge variant="destructive" className="ml-2">{overdueRemindersCount}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="suggestions" className="flex-1">
                  <CalendarDays className="w-4 h-4 mr-2" />
                  Tareas Sugeridas
                  {suggestionCount > 0 && (
                    <Badge variant="primary" className="ml-2">{suggestionCount}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="p-4 overflow-y-auto" style={{ maxHeight: "calc(400px - 48px)" }}>
              <TabsContent value="tasks" className="m-0">
                {consolidatedItems.filter(item => item.type === 'task').map(renderOverdueItem)}
                {consolidatedItems.filter(item => item.type === 'task').length === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    No hay tareas vencidas
                  </div>
                )}
              </TabsContent>

              <TabsContent value="reminders" className="m-0">
                {consolidatedItems
                  .filter(item => item.type === 'reminder' || item.type === 'progress')
                  .map(renderOverdueItem)}
                {consolidatedItems.filter(item => item.type === 'reminder' || item.type === 'progress').length === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    No hay recordatorios vencidos
                  </div>
                )}
              </TabsContent>

              <TabsContent value="suggestions" className="m-0">
                {taskSuggestions.length === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    No hay tareas sugeridas
                  </div>
                )}
                {taskSuggestions.map(renderSuggestionItem)}
              </TabsContent>
            </div>
          </Tabs>
        </div>
        
        <div className="p-4 border-t border-gray-200 flex justify-between items-center">
          <div className="bg-gray-100 rounded-full py-1 px-3 text-sm text-gray-600">
            {totalItems} elementos pendientes
          </div>
          <div className="flex gap-2">
            <button 
              className="px-4 py-1.5 text-sm font-medium rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50"
              onClick={handleRemindLater}
            >
              Recuérdame después
            </button>
            <button 
              className="px-4 py-1.5 text-sm font-medium rounded-full bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => setShowOverdueDialog(false)}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
      
      <AlertDialog open={!!deletingItemId} onOpenChange={(open) => !open && cancelDelete()}>
        <AlertDialogContent className="z-[1100]">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingItemType === 'task' 
                ? "Esta acción archivará la tarea. Puedes verla más tarde activando el filtro de tareas archivadas."
                : deletingItemType === 'suggestion'
                ? "Esta acción eliminará permanentemente esta sugerencia de tarea."
                : "Esta acción eliminará permanentemente este elemento y no se puede deshacer."
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDelete}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              {deletingItemType === 'task' ? "Archivar" : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OverdueTasksDialog;
